/**
 * GlobalPresence — reusable global section.
 * Used on ContactPage (bottom) and adjacent to Footer.
 *
 * Dark mode: slate-900 bg, white text pills, dark glass tooltip
 * Light mode: white bg, slate pills, white card tooltip
 *
 * Tooltip uses createPortal + position:fixed — NEVER clips regardless of parent overflow.
 * CMS: GET /wp-json/otpl/v1/global-presence
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '@/api/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Location {
  city: string;
  officeName: string;
  address: string;
  mapUrl: string;
  displayOrder: number;
}
interface Country {
  id: number;
  name: string;
  image: string;
  displayOrder: number;
  locations: Location[];
}

const FALLBACK: Country[] = [
  {
    id: 1, name: 'India', image: '', displayOrder: 1,
    locations: [
      { city: 'Noida',  officeName: 'Corporate Headquarters', address: '2nd Floor, Smartworks, Tower-D, Logix Cyber Park, Sector 62, Noida 201301',   mapUrl: 'https://maps.google.com/?q=Logix+Cyber+Park+Sector+62+Noida', displayOrder: 1 },
      { city: 'Pune',   officeName: 'West India Office',       address: 'Baner Road, Baner, Pune, Maharashtra 411045',                                mapUrl: 'https://maps.google.com/?q=Baner+Pune',                        displayOrder: 2 },
      { city: 'Indore', officeName: 'Central India Office',    address: 'Scheme 78 Part 2, Vijay Nagar, Indore, Madhya Pradesh 452010',                mapUrl: 'https://maps.google.com/?q=Vijay+Nagar+Indore',                displayOrder: 3 },
      { city: 'Mumbai', officeName: 'Mumbai Office',           address: 'Bandra Kurla Complex, Bandra East, Mumbai, Maharashtra 400051',              mapUrl: 'https://maps.google.com/?q=BKC+Mumbai',                        displayOrder: 4 },
    ],
  },
  {
    id: 2, name: 'UAE', image: '', displayOrder: 2,
    locations: [
      { city: 'Dubai', officeName: 'International Office', address: 'Level 14, Boulevard Plaza Tower 1, Downtown Dubai, UAE', mapUrl: 'https://maps.google.com/?q=Boulevard+Plaza+Tower+1+Dubai', displayOrder: 1 },
    ],
  },
];

// ─── Country avatar (flag-style circle) ───────────────────────────────────────
function CountryAvatar({ name, image }: { name: string; image: string }) {
  const initials = name.slice(0, 2).toUpperCase();
  if (image) return (
    <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 ring-1 ring-slate-200 dark:ring-white/20">
      <img src={image} alt={name} className="w-full h-full object-cover" loading="lazy" />
    </div>
  );
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white text-[10px] font-black tracking-wide ring-1 ring-slate-200 dark:ring-white/10"
      style={{ background: 'linear-gradient(135deg, var(--brand-primary,#2563eb), var(--brand-accent,#06b6d4))' }}
    >
      {initials}
    </div>
  );
}

// ─── Fixed-position portal tooltip ───────────────────────────────────────────
interface TooltipProps {
  location: Location;
  anchor: DOMRect;
  onEnter: () => void;
  onLeave: () => void;
}

function Tooltip({ location, anchor, onEnter, onLeave }: TooltipProps) {
  const W = 260;
  const GAP = 8;
  // Rough max height of the fully-populated card (office name + city +
  // multi-line address + button + paddings). Used only to decide whether
  // there's enough room above — the actual position below never depends
  // on this estimate (see `bottom` anchoring below), so a slightly-off
  // estimate can't cause the overlap bug this replaces.
  const EST_HEIGHT = 300;

  // Horizontal: centre on anchor, clamp to viewport
  let left = anchor.left + anchor.width / 2 - W / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - W - 8));

  // Vertical: prefer above, fallback below when there truly isn't room.
  const spaceAbove = anchor.top;
  const showAbove = spaceAbove > EST_HEIGHT + GAP;

  // Key fix: a `position: fixed` box always grows DOWNWARD from `top`,
  // so anchoring the "show above" case with `top: anchor.top - GAP` made
  // a ~250-300px-tall tooltip fall right back down over the pill row
  // (hiding sibling locations). Anchoring with `bottom` instead pins the
  // tooltip's bottom edge just above the pill, so it grows upward
  // regardless of its real height — no overlap with the row below it.
  const positionStyle = showAbove
    ? { bottom: window.innerHeight - anchor.top + GAP }
    : { top: anchor.bottom + GAP };

  const node = (
    <motion.div
      initial={{ opacity: 0, y: showAbove ? 8 : -8, scale: 0.94 }}
      animate={{ opacity: 1, y: 0,                   scale: 1    }}
      exit={{   opacity: 0, y: showAbove ? 5 : -5,   scale: 0.96 }}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ position: 'fixed', ...positionStyle, left, width: W, zIndex: 99999, transformOrigin: showAbove ? 'bottom center' : 'top center' }}
    >
      {/* Arrow when tooltip is BELOW pill */}
      {!showAbove && (
        <div className="flex justify-center mb-1" aria-hidden="true">
          <div className="w-2.5 h-2.5 rotate-45 rounded-sm
            bg-white dark:bg-slate-800
            border-l border-t border-slate-200 dark:border-white/10" />
        </div>
      )}

      {/* Card */}
      <div className="rounded-2xl overflow-hidden shadow-xl
        bg-white dark:bg-slate-800
        border border-slate-200 dark:border-white/10
        shadow-slate-200/60 dark:shadow-black/50">

        {/* Brand top bar */}
        <div className="h-[2px]"
          style={{ background: 'linear-gradient(to right, var(--brand-primary,#2563eb), var(--brand-accent,#06b6d4))' }} />

        <div className="p-4">
          {/* Office name */}
          {location.officeName && (
            <motion.p
              initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
              className="text-[10px] font-bold uppercase tracking-widest mb-1"
              style={{ color: 'var(--brand-accent,#06b6d4)' }}>
              {location.officeName}
            </motion.p>
          )}

          {/* City */}
          <motion.p
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
            className="font-display text-sm font-bold text-slate-900 dark:text-white mb-2.5 leading-snug">
            {location.city}
          </motion.p>

          {/* Address */}
          {location.address && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="flex items-start gap-2 mb-3">
              <svg className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{location.address}</p>
            </motion.div>
          )}

          {/* Map button */}
          {location.mapUrl && (
            <motion.a
              initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}
              href={location.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2 w-full rounded-xl py-2.5 text-xs font-semibold text-white transition-all hover:scale-[1.02] hover:shadow-lg"
              style={{
                background: 'linear-gradient(135deg, var(--brand-primary,#2563eb), var(--brand-accent,#06b6d4))',
                boxShadow: '0 4px 14px color-mix(in srgb, var(--brand-primary,#2563eb) 35%, transparent)',
              }}>
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              View on Map
              <svg className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </motion.a>
          )}
        </div>
      </div>

      {/* Arrow when tooltip is ABOVE pill */}
      {showAbove && (
        <div className="flex justify-center mt-1" aria-hidden="true">
          <div className="w-2.5 h-2.5 rotate-45 rounded-sm
            bg-white dark:bg-slate-800
            border-r border-b border-slate-200 dark:border-white/10" />
        </div>
      )}
    </motion.div>
  );

  return createPortal(node, document.body);
}

// ─── Location pill ─────────────────────────────────────────────────────────
function LocationPill({ location, index }: { location: Location; index: number }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const btnRef  = useRef<HTMLButtonElement>(null);
  const openT   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT  = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeT.current) { clearTimeout(closeT.current); closeT.current = null; }
  }, []);

  const scheduleClose = useCallback(() => {
    closeT.current = setTimeout(() => { setOpen(false); setAnchor(null); }, 300);
  }, []);

  function openTooltip() {
    cancelClose();
    if (btnRef.current) setAnchor(btnRef.current.getBoundingClientRect());
    setOpen(true);
  }

  function onMouseEnter() {
    cancelClose();
    if (openT.current) clearTimeout(openT.current);
    openT.current = setTimeout(openTooltip, 80);
  }
  function onMouseLeave() {
    if (openT.current) clearTimeout(openT.current);
    scheduleClose();
  }
  function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) { setOpen(false); setAnchor(null); cancelClose(); }
    else openTooltip();
  }

  // Update anchor on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => { if (btnRef.current) setAnchor(btnRef.current.getBoundingClientRect()); };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => { window.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [open]);

  return (
    <span className="relative inline-flex">
      <motion.button
        ref={btnRef}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${location.city} office details`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.04 + index * 0.05, duration: 0.26, ease: 'easeOut' }}
        className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium
          outline-none focus-visible:ring-2 select-none whitespace-nowrap
          transition-all duration-150
          bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200
          dark:bg-slate-700/60 dark:hover:bg-slate-600/70 dark:text-slate-200 dark:border-slate-600
          focus-visible:ring-blue-500/40"
        style={open ? {
          background: 'color-mix(in srgb, var(--brand-primary,#2563eb) 14%, transparent)',
          borderColor: 'color-mix(in srgb, var(--brand-primary,#2563eb) 50%, transparent)',
          color: 'var(--brand-primary,#2563eb)',
        } : {}}
      >
        <motion.svg
          className="h-3 w-3 shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
          animate={{ scale: open ? 1.3 : 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 18 }}
          style={{ color: open ? 'var(--brand-accent,#06b6d4)' : 'currentColor', opacity: open ? 1 : 0.55 }}
        >
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
        </motion.svg>
        <span>{location.city}</span>
      </motion.button>

      <AnimatePresence>
        {open && anchor && (
          <Tooltip
            location={location}
            anchor={anchor}
            onEnter={cancelClose}
            onLeave={scheduleClose}
          />
        )}
      </AnimatePresence>
    </span>
  );
}

// ─── Country group — single inline row: flag + name + office-count + pills ────
function CountryGroup({ country, index, isLast }: { country: Country; index: number; isLast: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-30px' }}
      transition={{ delay: index * 0.09, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="flex w-full flex-col items-start gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center"
    >
      {/* Header row: flag + name + office count (stays together on mobile) */}
      <div className="flex items-center gap-2.5 shrink-0">
        <CountryAvatar name={country.name} image={country.image} />
        <span className="font-display text-base font-bold text-slate-900 dark:text-white">{country.name}</span>
        {/* Office count pill */}
        <span className="inline-flex items-center gap-1.5 pl-3 rounded-full text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0">
          <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l8-4v18M13 21V11h6v10M9 9v.01M9 12v.01M9 15v.01" />
          </svg>
          {country.locations.length} {country.locations.length === 1 ? 'Office' : 'Offices'}
        </span>
      </div>

      {/* Location pills — full width below the header on mobile, inline on desktop */}
      <div className="flex flex-wrap items-center gap-2">
        {country.locations.map((loc, li) => (
          <LocationPill key={`${country.id}-${li}`} location={loc} index={li} />
        ))}
      </div>

      {/* Vertical divider between countries (desktop only) */}
      {!isLast && (
        <span aria-hidden className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700/60 mx-2" />
      )}
    </motion.div>
  );
}

// ─── Exported component ───────────────────────────────────────────────────────
export function GlobalPresence() {
  const [countries, setCountries] = useState<Country[]>(FALLBACK);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    apiClient.get<Country[]>('/wp-json/otpl/v1/global-presence')
      .then(r => { if (Array.isArray(r.data) && r.data.length) setCountries(r.data); })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  if (!ready) return null;

  return (
    <section className="relative">

      <div className="relative mx-auto px-4 sm:px-6 py-8 lg:py-10"
        style={{ maxWidth: 'var(--ui-container-width, 1280px)' }}>

        {/* Countries: stacked full-width rows on mobile, single flowing centered row on desktop */}
        <div className="flex flex-col items-start gap-y-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-y-5 sm:gap-x-3">
          {countries.map((c, i) => (
            <CountryGroup
              key={c.id}
              country={c}
              index={i}
              isLast={i === countries.length - 1}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
